import { AfterViewInit, Component, Inject, OnInit, ViewChild, HostBinding } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { MediaService } from '@core/services/media.service';

import { BillingInvoiceModel as BillingInvoice } from '@shared/models/billing.model';

import { ApiResponseModel as ApiResponse } from '@shared/models/services/api.response.model';
import { BillingHistoryResponseData } from '@shared/models/services/billing-service.response.model';
import { CdkVirtualScrollableElement } from '@angular/cdk/scrolling';
import { filter, map, pairwise, throttleTime } from 'rxjs';

interface ListEntry {
    invoice?: BillingInvoice | undefined;
    date?: string | undefined;
}

@Component({
    selector: 'sfx-billing-usage-history',
    templateUrl: './history.component.html',
    styleUrls: ['./history.component.scss']
})
export class BillingUsageHistoryComponent implements OnInit, AfterViewInit {

    // Set flex attributes on the host selector
    @HostBinding('attr.data-flex') public readonly dataFlexAttr: string = 'grow';
    @HostBinding('attr.data-layout') public readonly layoutAttr: string = 'column';

    public invoices: BillingInvoice[] = [];
    public entries: ListEntry[] = [];

    @ViewChild(CdkVirtualScrollableElement)
    scrollable!: CdkVirtualScrollableElement;

    public rowSize = 48;

    private lastInvoiceProcessedUnixTimestamp: number | undefined;
    private hasMore = false;
    private typeMap: { [key: string]: string } = {};

    private invoiceDescriptionTranslations: { [key: string]: (params: string[] | undefined) => string }

    private $filter: any;

    constructor(
        private datePipe: DatePipe,
        private translate: TranslateService,
        public media: MediaService,
        @Inject('billingService') public billingService: any,
        @Inject('rootScope') public rootScope: any,
        @Inject('utilsService') public utils: any,
        @Inject('$filter') $filter: any
    ) {
        this.$filter = $filter;
    }

    public ngOnInit(): void {

        if (this.rootScope.history[this.rootScope.history.length - 1].fromState.name.includes('billingSettings.')) {
            this.rootScope.history.pop();
        }

        this.typeMap = {
            'credited': this.translate.instant('Credited'),
            'debited': this.translate.instant('Debited balance by'),
            'charge': this.translate.instant('Charged')
        };

        this.invoiceDescriptionTranslations = {
            'Initial subscription': () => this.translate.instant('Initial subscription'),
            'Annual subscription renewal': () => this.translate.instant('Annual subscription renewal'),
            'Monthly subscription renewal': () => this.translate.instant('Monthly subscription renewal'),
            'Switched from {{ fromUsers }} to {{ toUsers }} users': (params) => this.translate.instant('Switched from {{ fromUsers }} to {{ toUsers }} users', { fromUsers: params?.[0], toUsers: params?.[1] }),
            'Switched from the {{ fromPlan }} to the {{ toPlan }}': (params) => this.translate.instant('Switched from the {{ fromPlan }} to the {{ toPlan }}', { fromPlan: params?.[0], toPlan: params?.[1] })
        };

        this.loadBatch();
    }

    public ngAfterViewInit(): void {

        this.scrollable.elementScrolled().pipe(
            map(() => this.scrollable.measureScrollOffset('bottom')),
            pairwise(),
            filter(([previousBottomOffset, currentBottomOffset]) => (this.hasMore && currentBottomOffset < previousBottomOffset && currentBottomOffset < this.rowSize * 2)),
            throttleTime(500)
        ).subscribe(() => {
            this.loadBatch();
        });
    }

    public loadBatch(): void {
        this.billingService.getBillingHistory(this.lastInvoiceProcessedUnixTimestamp).then(this._billingHistoryResponseHandler.bind(this));
    }

    public isSameMonth(firstDate: string | number, secondDate: string | number) {
        return this.utils.isSameMonth(firstDate, secondDate);
    }

    public isSameDay(firstDate: string | number, secondDate: string | number) {
        return this.utils.isSameDay(firstDate, secondDate);
    }

    public previousIndex(index: number): number {
        return Math.max(index - 1, 0);
    }

    public formatMonthYear(dateString: string): string {
        if (!dateString) {
            return '';
        }

        const date = new Date(dateString + 'T00:00:00');
        const formatMonthYearFilter = this.$filter('formatMonthYear');
        return formatMonthYearFilter(date) || '';
    }

    public formatDayMonth(dateString: string): string {
        if (!dateString) {
            return '';
        }

        const date = new Date(dateString + 'T00:00:00');
        const formatSimpleDate = this.$filter('formatSimpleDate');
        return formatSimpleDate(date, false) || '';
    }

    private _billingHistoryResponseHandler(response: ApiResponse<BillingHistoryResponseData>): void {

        this.lastInvoiceProcessedUnixTimestamp = response.data.last_invoice_processed_timestamp;
        this.hasMore = response.data.has_more;

        if (response.data.lines) {

            this.invoices = [...this.invoices, ...response.data.lines.map((invoice) => {

                if (invoice.description === 'Monthly accrued balance' && invoice.type === 'credited') {
                    return;
                }

                let description = invoice.baseDescription || invoice.description;
                description = description && description.split(' | VAT')[0].split('\n')[0];

                if (description) {
                    description = this.invoiceDescriptionTranslations[description]?.(invoice.translationParams) || description;
                }
                else {
                    description = this.translate.instant('No description available');
                }

                return {
                    type: invoice.type,
                    chargeLine: this.typeMap[invoice.type],
                    unixTime: invoice.date,
                    date: this.datePipe.transform(new Date(invoice.date * 1000), 'yyyy-MM-dd'),
                    total: invoice.total / 100,
                    description,
                    currency: invoice.currency === 'eur' ? 'EUR' : 'USD',
                    invoice_pdf: invoice.invoice_pdf
                };
            }).filter((invoice) => !!invoice) as BillingInvoice[]];

            this.entries = this.invoices.flatMap((invoice, idx) => {

                if (idx === 0) {
                    return [{ date: invoice.date }, { invoice }];
                }
                else {
                    const previousEntry = this.invoices[idx - 1];

                    if (this.isSameMonth(previousEntry.date, invoice.date)) {

                        if (this.isSameDay(previousEntry.date, invoice.date)) {
                            invoice.hideDate = true;
                        }

                        return [{ invoice }];
                    }
                    else {
                        return [{ date: invoice.date }, { invoice }];
                    }
                }
            }) as ListEntry[];
        }
    }
}
